import React, {Component} from 'react';
import * as R from 'ramda';
import moment from 'moment';
import t from 'tcomb-form';
import classNames from 'classnames';

// Icons
import yesActiveImg from 'images/publisher/profile/yes-active.png';
import yesInactiveImg from 'images/publisher/profile/yes-inactive.png';
import noActiveImg from 'images/publisher/profile/no-active.png';
import noInactiveImg from 'images/publisher/profile/no-inactive.png';

import maleActiveImg from 'images/publisher/profile/male-active.png';
import maleInactiveImg from 'images/publisher/profile/male-inactive.png';
import femaleActiveImg from 'images/publisher/profile/female-active.png';
import femaleInactiveImg from 'images/publisher/profile/female-inactive.png';

import 'scss/form/component/form_children';

const mapIndexed = R.addIndex(R.map);

export default class FormChildren extends Component {
    static defaultProps = {options: {}, ctx: {}};

    constructor(props) {
        super(props);

        const children = props.value;
        const hasAnswered = !R.isNil(props.value);
        const hasChildren = hasAnswered ? R.length(props.value) > 0 : false;

        this.state = {
            children,
            hasAnswered,
            hasChildren,
            hasError: false
        };
    }

    setHasChildren = hasChildren => {
        const {children} = this.state;

        let newChildren;
        if (R.isNil(children) || R.isEmpty(children)) {
            newChildren = [{year_born: null}];
        } else {
            newChildren = children;
        }

        this.setState(
            {
                children: hasChildren ? newChildren : [],
                hasAnswered: true,
                hasChildren
            },
            this.onChange
        );
    };

    handleYearBornSelect = (e, idx) => {
        const {children} = this.state;

        const newChildren = R.adjust(
            R.assocPath(['year_born'], parseInt(e.target.value, 10)),
            idx,
            children
        );

        this.setState({children: newChildren}, this.onChange);
    };

    handleGenderSelect = (gender, idx) => {
        const {children} = this.state;

        const newChildren = R.adjust(R.assocPath(['gender'], gender), idx, children);

        this.setState({children: newChildren}, this.onChange);
    };

    addChild = () => {
        const {children} = this.state;

        const newChild = [{year_born: null, gender: null}];

        this.setState(
            {
                children: R.concat(children, newChild)
            },
            this.onChange
        );
    };

    removeChild = idx => {
        const {children} = this.state;

        const newChildren = [...children.slice(0, idx), ...children.slice(idx + 1)];

        this.setState(
            {
                children: newChildren
            },
            this.onChange
        );
    };

    /** Be sure to call this AFTER a setState has completed. */
    onChange = () => {
        this.props.onChange(this.state.children, this.props.ctx.path);
    };

    getChildrenData = () => {
        const {hasChildren, children} = this.state;

        if (R.isNil(children)) {
            return null;
        } else if (hasChildren) {
            return children;
        }
        return [];
    };

    getValidationOptions = () => ({
        path: this.props.ctx.path,
        context: t.mixin(t.mixin({}, this.props.context || this.props.ctx.context), {
            options: this.props.options
        })
    });

    validate = () => {
        const childrenData = this.getChildrenData();
        const result = t.validate(childrenData, this.props.type, this.getValidationOptions());

        this.setState({
            hasError: !result.isValid()
        });

        return result;
    };

    getIconImgs = () => {
        const {hasAnswered, hasChildren} = this.state;

        let yesIconImg;
        let noIconImg;

        if (hasAnswered) {
            yesIconImg = hasChildren ? yesActiveImg : yesInactiveImg;
            noIconImg = !hasChildren ? noActiveImg : noInactiveImg;
        } else {
            yesIconImg = yesInactiveImg;
            noIconImg = noInactiveImg;
        }

        return [yesIconImg, noIconImg];
    };

    displayYesNoSelection = () => {
        const {hasAnswered, hasChildren} = this.state;

        const yesButtonClasses = classNames('selection', 'selection-yes', {
            selected: hasAnswered && hasChildren
        });
        const noButtonClasses = classNames('selection', 'selection-no', {
            selected: hasAnswered && !hasChildren
        });
        const [yesIconImg, noIconImg] = this.getIconImgs();

        return (
            <div className="children-selection">
                <div
                    className={yesButtonClasses}
                    onClick={() => {
                        this.setHasChildren(true);
                    }}
                >
                    <img className="selection-icon" src={yesIconImg} />
                    <div className="selection-text">YES</div>
                </div>
                <div
                    className={noButtonClasses}
                    onClick={() => {
                        this.setHasChildren(false);
                    }}
                >
                    <img className="selection-icon" src={noIconImg} />
                    <div className="selection-text">NO</div>
                </div>
            </div>
        );
    };

    yearOptions = () => {
        const year = moment().get('year');
        return R.pipe(
            R.map(i => (
                <option key={i} value={i}>
                    {i}
                </option>
            )),
            R.reverse,
            R.prepend(<option>Select Year</option>)
        )(R.range(year - 100, year + 1));
    };

    displayChildrenSelects = () => {
        const {children} = this.state;

        if (R.isNil(children)) return;

        return mapIndexed((child, idx) => {
            const {gender} = child;

            const maleButtonClasses = classNames('selection', 'selection-yes', {
                selected: gender === 'male'
            });
            const femaleButtonClasses = classNames('selection', 'selection-no', {
                selected: gender === 'female'
            });

            let maleIconImg;
            let femaleIconImg;

            if (child.gender) {
                maleIconImg = child.gender === 'male' ? maleActiveImg : maleInactiveImg;
                femaleIconImg = child.gender === 'female' ? femaleActiveImg : femaleInactiveImg;
            } else {
                maleIconImg = maleInactiveImg;
                femaleIconImg = femaleInactiveImg;
            }

            const removeChildButton =
                children.length > 1 ? (
                    <i
                        onClick={() => this.removeChild(idx)}
                        className="fa fa-trash-o remove-child"
                        aria-hidden="true"
                    />
                ) : null;

            return (
                <div>
                    <div className="child-year-dropdown">
                        <div className="child-info">
                            <div className="child-count">{`Child ${idx + 1}`}</div>
                            {removeChildButton}
                        </div>
                        <select
                            id="year-born-select"
                            onChange={e => this.handleYearBornSelect(e, idx)}
                            defaultValue={child.year_born}
                            value={child.year_born}
                            key={`year-born-${idx}`}
                        >
                            {this.yearOptions()}
                        </select>
                    </div>
                    <div className="children-selection">
                        <div
                            className={maleButtonClasses}
                            onClick={() => {
                                this.handleGenderSelect('male', idx);
                            }}
                        >
                            <img className="selection-icon" src={maleIconImg} />
                            <div className="selection-text">MALE</div>
                        </div>
                        <div
                            className={femaleButtonClasses}
                            onClick={() => {
                                this.handleGenderSelect('female', idx);
                            }}
                        >
                            <img className="selection-icon" src={femaleIconImg} />
                            <div className="selection-text">FEMALE</div>
                        </div>
                    </div>
                </div>
            );
        }, children);
    };

    displayChildrenDetails = () => {
        const {hasChildren} = this.state;

        const childrenSelects = this.displayChildrenSelects();

        return hasChildren ? (
            <div className="children-details">
                <label className="control-label child-born-prompt">
                    What year was your child born?
                </label>

                {childrenSelects}

                <div className="add-another-child" onClick={this.addChild}>
                    + Add another child
                </div>
            </div>
        ) : null;
    };

    render() {
        const {hasError} = this.state;
        const label = this.props.options.label || this.props.ctx.label;

        const selection = this.displayYesNoSelection();
        const childrenDetails = this.displayChildrenDetails();

        return (
            <div className={classNames('form-group', hasError && 'has-error')}>
                <div className="form-children">
                    <label className="control-label">{label}</label>

                    {selection}
                    {childrenDetails}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./form_components/form_children.js